import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ENGINE_VERSION, STAGE_LABEL, ScenarioSchema, type CalcResult, type Scenario } from "@h2lca/core";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { ButtonLink, Button, SelectField, TextArea, ToggleButton } from "@/components/ui/FormControls";
import { DataConnectionsPanel, type DataConnection } from "@/components/workspace/DataConnectionsPanel";
import { ImportPanel, type ImportResult } from "@/components/workspace/ImportPanel";
import { ImportApplyPanel } from "@/components/workspace/ImportApplyPanel";
import { MasterDataComparison } from "@/components/workspace/MasterDataComparison";
import type { ImportRecord } from "@/components/workspace/import-template";
import { compareRunConditions } from "@/components/workspace/run-comparison";
import { api } from "@/lib/api";
import { useResult } from "@/store/useResult";
import { useWorkshop } from "@/store/workshop";

interface ImportSummary extends ImportResult {
  connectionId: string | null;
  applications: number;
}
interface RunSummary {
  id: string;
  status: string;
  engineVersion: string;
  masterFingerprint: string;
  note: string | null;
  reviewStatus: string;
  isBaseline: boolean;
  sourceImportIds: unknown;
  summary: { status: string; totalKgCO2eq: number; perKgH2: number | null; perFunctionalUnitKgCO2eq?: number | null; functionalUnitKg?: number; issueCount: number; method: string; denominatorKg: number };
  createdAt: string;
}
interface RunDetail extends RunSummary {
  inputSnapshot: Scenario;
  masterSnapshot: unknown;
  result: CalcResult;
}
interface ProjectPayload {
  project: { id: string; name: string; siteName: string; createdAt: string; updatedAt: string };
  imports: ImportSummary[];
  runs: RunSummary[];
}

interface ComparedInput {
  equipment: string;
  label: string;
  unit: string;
  left: number | null;
  right: number | null;
  delta: number | null;
}

function numberText(value: number | null | undefined) {
  return value === null || value === undefined ? "계산 제한" : new Intl.NumberFormat("ko-KR", { maximumFractionDigits: 3 }).format(value);
}

function dateText(value: string) {
  return new Date(value).toLocaleString("ko-KR", { dateStyle: "medium", timeStyle: "short" });
}

function importStatus(status: string) {
  if (status === "ready") return "검증 완료";
  if (status === "review") return "검토 필요";
  return "오류 확인 필요";
}

function amountText(value: number | null, unit: string) {
  return value === null ? "없음" : `${numberText(value)} ${unit}`;
}

function deltaText(value: number | null) {
  if (value === null) return "계산 제한";
  if (value === 0) return "변화 없음";
  return `${value > 0 ? "+" : ""}${numberText(value)}`;
}

function reviewStatusText(value: string) {
  if (value === "approved") return "검토 완료";
  if (value === "reviewing") return "검토 중";
  return "초안";
}

function changedInputs(left: RunDetail, right: RunDetail): ComparedInput[] {
  const collect = (run: RunDetail) => {
    const rows = new Map<string, { equipment: string; label: string; unit: string; amount: number | null }>();
    for (const equipment of run.inputSnapshot.equipment) {
      for (const input of equipment.inputs) {
        rows.set(`${equipment.name}::${input.key}::${input.unit}`, { equipment: equipment.name, label: input.label, unit: input.unit, amount: input.amount });
      }
    }
    return rows;
  };

  const leftRows = collect(left);
  const rightRows = collect(right);
  const keys = new Set([...leftRows.keys(), ...rightRows.keys()]);
  return [...keys]
    .map((key) => {
      const a = leftRows.get(key);
      const b = rightRows.get(key);
      const leftAmount = a?.amount ?? null;
      const rightAmount = b?.amount ?? null;
      return {
        equipment: a?.equipment ?? b?.equipment ?? "알 수 없는 설비",
        label: a?.label ?? b?.label ?? "알 수 없는 항목",
        unit: a?.unit ?? b?.unit ?? "",
        left: leftAmount,
        right: rightAmount,
        delta: leftAmount !== null && rightAmount !== null ? rightAmount - leftAmount : null,
      };
    })
    .filter((row) => row.left !== row.right)
    .sort((a, b) => Math.abs(b.delta ?? 0) - Math.abs(a.delta ?? 0));
}

export function WorkspaceProjectPage() {
  const { projectId } = useParams();
  const nav = useNavigate();
  const scenario = useWorkshop((s) => s.scenario);
  const loadScenario = useWorkshop((s) => s.loadScenario);
  const updateScenario = useWorkshop((s) => s.update);
  const restoreDraft = useWorkshop((s) => s.restoreDraft);
  const result = useResult();
  const [data, setData] = useState<ProjectPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [selectedRun, setSelectedRun] = useState<RunDetail | null>(null);
  const [loadingRunId, setLoadingRunId] = useState<string | null>(null);
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [compareRuns, setCompareRuns] = useState<[RunDetail, RunDetail] | null>(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const [connections, setConnections] = useState<DataConnection[]>([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState("");
  const [latestImportId, setLatestImportId] = useState<string | null>(null);
  const [latestImportRecords, setLatestImportRecords] = useState<ImportRecord[]>([]);
  const [lineage, setLineage] = useState<{ importId: string; applications: Array<{ id: string; appliedRows: number; skippedRows: number; createdAt: string; createdBy: { email: string; name: string }; mapping: unknown }> } | null>(null);

  async function load() {
    if (!projectId) return;
    try {
      setError(null);
      setData(await api<ProjectPayload>(`/workspace/projects/${projectId}`));
    } catch (e) {
      setError(e instanceof Error ? e.message : "프로젝트를 불러오지 못했습니다.");
    }
  }

  async function loadConnections() {
    if (!projectId) return;
    try {
      const response = await api<{ connections: DataConnection[] }>(`/workspace/projects/${projectId}/connections`);
      setConnections(response.connections);
    } catch {
      setConnections([]);
    }
  }

  useEffect(() => {
    if (!useWorkshop.getState().scenario) restoreDraft();
    void load();
    void loadConnections();
  }, [projectId, restoreDraft]);

  async function runCurrentScenario() {
    if (!projectId || !scenario) return;
    setRunning(true);
    setNotice(null);
    setError(null);
    try {
      const response = await api<{ run: RunSummary; result: CalcResult }>(`/workspace/projects/${projectId}/runs`, { method: "POST", json: { scenario, importId: latestImportId ?? undefined } });
      setNotice(`산정 실행 ${response.run.id}을 저장했습니다. ${response.result.perKgH2 === null ? "현재 결과는 계산 제한 상태입니다." : `1 kg당 ${numberText(response.result.perKgH2)} kgCO2eq/kgH2입니다.`}`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "산정 실행을 저장하지 못했습니다.");
    } finally {
      setRunning(false);
    }
  }

  async function openLineage(importId: string) {
    if (!projectId) return;
    try {
      const response = await api<{ applications: Array<{ id: string; appliedRows: number; skippedRows: number; createdAt: string; createdBy: { email: string; name: string }; mapping: unknown }> }>(`/workspace/projects/${projectId}/imports/${importId}/lineage`);
      setLineage({ importId, applications: response.applications });
    } catch (e) {
      setError(e instanceof Error ? e.message : "반영 이력을 불러오지 못했습니다.");
    }
  }

  async function updateRun(patch: { note?: string | null; reviewStatus?: "draft" | "reviewing" | "approved"; isBaseline?: boolean }) {
    if (!projectId || !selectedRun) return;
    try {
      const response = await api<{ run: { id: string; note: string | null; reviewStatus: string; isBaseline: boolean } }>(`/workspace/projects/${projectId}/runs/${selectedRun.id}`, { method: "PATCH", json: patch });
      setSelectedRun((current) => current ? { ...current, ...response.run } : current);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "실행 기록을 변경하지 못했습니다.");
    }
  }

  async function rerunSelected() {
    if (!projectId || !selectedRun) return;
    try {
      const response = await api<{ run: RunSummary }>(`/workspace/projects/${projectId}/runs/${selectedRun.id}/re-run`, { method: "POST" });
      setNotice(`현재 마스터 데이터 기준으로 실행 기록 ${response.run.id}을 새로 저장했습니다.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "실행 기록을 다시 만들지 못했습니다.");
    }
  }

  async function openRun(runId: string) {
    if (!projectId) return;
    setLoadingRunId(runId);
    setError(null);
    try {
      const response = await api<{ run: RunDetail }>(`/workspace/projects/${projectId}/runs/${runId}`);
      setSelectedRun(response.run);
    } catch (e) {
      setError(e instanceof Error ? e.message : "실행 기록을 불러오지 못했습니다.");
    } finally {
      setLoadingRunId(null);
    }
  }

  function toggleCompare(runId: string) {
    setCompareRuns(null);
    setCompareIds((current) => {
      if (current.includes(runId)) return current.filter((id) => id !== runId);
      if (current.length >= 2) {
        setNotice("비교할 실행 기록은 두 개까지 선택할 수 있습니다.");
        return current;
      }
      return [...current, runId];
    });
  }

  async function openComparison() {
    if (!projectId || compareIds.length !== 2) return;
    setLoadingComparison(true);
    setError(null);
    try {
      const details = await Promise.all(compareIds.map((id) => api<{ run: RunDetail }>(`/workspace/projects/${projectId}/runs/${id}`)));
      const [left, right] = details;
      if (!left || !right) throw new Error("비교할 실행 기록을 불러오지 못했습니다.");
      setCompareRuns([left.run, right.run]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "실행 기록을 비교하지 못했습니다.");
    } finally {
      setLoadingComparison(false);
    }
  }

  function loadRunScenario() {
    if (!selectedRun) return;
    const parsed = ScenarioSchema.safeParse(selectedRun.inputSnapshot);
    if (!parsed.success) {
      setError("실행 기록의 입력 시나리오를 열 수 없습니다.");
      return;
    }
    loadScenario(parsed.data, null);
    updateScenario((current) => current);
    nav("/app/work");
  }

  const comparison = compareRuns ? compareRunConditions(...compareRuns) : null;

  return (
    <AppShell>
      <div className="page">
        <PageHeader
          title={data?.project.name ?? "프로젝트"}
          description={data ? `${data.project.siteName}. 데이터와 산정 실행 기록을 관리합니다.` : "프로젝트 정보를 불러오는 중입니다."}
          actions={<ButtonLink to="/workspace" variant="secondary">작업공간으로</ButtonLink>}
        />
        {error && <p className="note note-danger text-caption" role="alert">{error}</p>}
        {notice && <p className="note note-ok text-caption" role="status">{notice}</p>}
        {data && (
          <>
            <section className="workspace-project-hero">
              <div><p className="workspace-kicker">PROJECT</p><h2>{data.project.siteName}</h2><p>실제 파일을 가져와 데이터 품질을 확인하고, 현재 산정 결과를 서버 실행 기록으로 남길 수 있습니다.</p></div>
              <div className="workspace-run-action"><span className={`badge-soft${scenario ? " badge-accent" : ""}`}>{scenario ? "현재 산정 불러옴" : "현재 산정 없음"}</span><Button variant="primary" disabled={!scenario || !result || running} onClick={() => void runCurrentScenario()}>{running ? "실행 기록 저장 중…" : "현재 산정을 실행 기록으로 저장"}</Button>{!scenario && <Link to="/app" className="text-footnote text-accent underline">산정 시작으로 이동</Link>}</div>
            </section>

            <div className="workspace-content-grid">
              <ImportPanel projectId={data.project.id} connections={connections} connectionId={selectedConnectionId} onConnectionChange={setSelectedConnectionId} onImported={(imported, records) => { setLatestImportId(imported.id); setLatestImportRecords(records); setNotice(`${imported.fileName}을 가져왔습니다. ${imported.validRows}행은 바로 사용 가능하고, ${imported.warningRows + imported.errorRows}행은 확인이 필요합니다.`); void load(); void loadConnections(); }} />
              <section className="workspace-card">
                <div className="workspace-card-head"><div><h2 className="workspace-card-title">데이터 품질</h2><p className="workspace-card-desc">자동 산정 전에 확인이 필요한 데이터가 있는지 보여 줍니다.</p></div></div>
                {data.imports.length === 0 ? <p className="workspace-empty">아직 가져온 데이터가 없습니다. 표준 CSV 또는 XLSX 파일을 올려 보세요.</p> : <div className="workspace-quality-list">{data.imports.slice(0, 5).map((item) => <div key={item.id} className="workspace-quality-row"><div><strong>{item.fileName}</strong><span>{item.period}, {dateText(item.createdAt)}</span></div><div className="workspace-quality-counts"><span className="badge-accent">정상 {item.validRows}</span>{item.warningRows > 0 && <span className="badge-review">검토 {item.warningRows}</span>}{item.errorRows > 0 && <span className="badge-danger">오류 {item.errorRows}</span>}{item.applications > 0 && <><span className="badge-muted">반영 {item.applications}</span><Button size="compact" onClick={() => void openLineage(item.id)}>반영 이력</Button></>}</div></div>)}</div>}
              </section>
            </div>

            {latestImportId && latestImportRecords.length > 0 && <ImportApplyPanel projectId={data.project.id} importId={latestImportId} records={latestImportRecords} scenario={scenario} onApplied={(next, updatedAt, appliedRows, skippedRows) => { loadScenario(next, useWorkshop.getState().serverId, updatedAt); setNotice(`${appliedRows}개 행을 현재 산정에 반영했습니다. ${skippedRows}개 행은 검토 대상으로 남겼습니다.`); void load(); }} />}
            <DataConnectionsPanel projectId={data.project.id} connections={connections} onChanged={() => { void loadConnections(); void load(); }} />
            {lineage && <section className="workspace-card"><div className="workspace-card-head"><div><h2 className="workspace-card-title">업로드 반영 이력</h2><p className="workspace-card-desc">반영된 행과 작업자를 확인합니다.</p></div><Button onClick={() => setLineage(null)}>닫기</Button></div>{lineage.applications.length === 0 ? <p className="workspace-empty">아직 산정 입력값에 반영된 기록이 없습니다.</p> : <div className="workspace-lineage-list">{lineage.applications.map((application) => <div className="workspace-lineage-row" key={application.id}><div><strong>{application.appliedRows}개 행 반영</strong><span>{application.createdBy.name} ({application.createdBy.email}), {dateText(application.createdAt)}</span></div><span className="badge-muted">검토 {application.skippedRows}개</span></div>)}</div>}</section>}

            <section className="workspace-card">
              <div className="workspace-card-head">
                <div><h2 className="workspace-card-title">산정 실행 기록 <span className="workspace-count">{data.runs.length}회</span></h2><p className="workspace-card-desc">실행 시점의 입력 시나리오와 마스터 데이터 스냅샷을 함께 보관합니다.</p></div>
                <div className="workspace-actions">
                  <span className="workspace-count" role="status">{compareIds.length} / 2개 선택</span>
                  <Button variant="soft" disabled={compareIds.length !== 2 || loadingComparison} onClick={() => void openComparison()}>{loadingComparison ? "비교 중" : "실행 기록 비교"}</Button>
                </div>
              </div>
              {data.runs.length === 0 ? (
                <p className="workspace-empty">아직 실행 기록이 없습니다. 현재 산정을 이 프로젝트에 저장해 보세요.</p>
              ) : (
                <div className="table-scroll">
                  <table className="admin-table workspace-runs-table">
                    <thead>
                      <tr><th>실행 시각</th><th>상태</th><th>검토</th><th>1 kg당 배출량</th><th>검증 이슈</th><th>엔진 및 데이터 버전</th><th>상세</th></tr>
                    </thead>
                    <tbody>
                      {data.runs.map((run) => (
                        <tr key={run.id}>
                          <td>{dateText(run.createdAt)}</td>
                          <td>
                            <div className="flex flex-wrap items-center gap-inline">
                              <span className={run.status === "ok" ? "badge-accent" : "badge-review"}>{run.status === "ok" ? "계산 가능" : "확인 필요"}</span>
                              {run.isBaseline && <span className="badge-muted">기준</span>}
                            </div>
                          </td>
                          <td><span className={run.reviewStatus === "approved" ? "badge-accent" : run.reviewStatus === "reviewing" ? "badge-review" : "badge-muted"}>{reviewStatusText(run.reviewStatus)}</span></td>
                          <td className="num">{run.engineVersion === ENGINE_VERSION ? <>{numberText(run.summary.perKgH2)} <span className="text-footnote text-ink-subtle">kgCO2eq/kgH2</span></> : "이전 엔진: 재실행 필요"}</td>
                          <td>{run.summary.issueCount}건</td>
                          <td><span className="font-mono text-footnote">{run.engineVersion}, {run.masterFingerprint}</span></td>
                          <td>
                            <div className="workspace-actions">
                              <Button size="compact" onClick={() => void openRun(run.id)} disabled={loadingRunId === run.id}>{loadingRunId === run.id ? "불러오는 중" : "열기"}</Button>
                              <ToggleButton size="compact" selected={compareIds.includes(run.id)} onClick={() => toggleCompare(run.id)}>비교 선택</ToggleButton>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {compareRuns && (
              <section className="workspace-card workspace-run-comparison" aria-labelledby="run-comparison-title">
                <div className="workspace-card-head">
                  <div>
                    <h2 id="run-comparison-title" className="workspace-card-title">실행 기록 비교</h2>
                    <p className="workspace-card-desc">첫 번째로 선택한 기록을 기준으로 두 번째 기록의 변화를 표시합니다.</p>
                  </div>
                  <Button onClick={() => { setCompareRuns(null); setCompareIds([]); }}>비교 닫기</Button>
                </div>
                {comparison && <>
                  {!comparison.comparable && <p role="status" className="note note-warn text-footnote">직접 감축 비교를 할 수 없습니다. 확인할 조건: {comparison.reasons.join(", ")}. 아래 총량·단계별 차이는 기록 간 차이이며 감축 성과를 뜻하지 않습니다.</p>}
                  <div className="workspace-comparison-section"><h3 className="workspace-card-title">산정조건 비교</h3><div className="table-scroll"><table className="admin-table workspace-condition-table"><thead><tr><th>조건</th><th>기준 기록</th><th>비교 기록</th><th>판정</th></tr></thead><tbody>{comparison.rows.map((row) => <tr key={row.label}><td className="whitespace-nowrap">{row.label}</td><td className="whitespace-pre-line">{row.left}</td><td className="whitespace-pre-line">{row.right}</td><td>{row.changed ? "다름" : "같음"}</td></tr>)}</tbody></table></div></div>
                </>}
                <div className="workspace-compare-summary">
                  <div><span>기준 기록 · {compareRuns[0].id}</span><strong>{dateText(compareRuns[0].createdAt)}</strong><span>{compareRuns[0].result.methodLabel}</span></div>
                  <div><span>비교 기록 · {compareRuns[1].id}</span><strong>{dateText(compareRuns[1].createdAt)}</strong><span>{compareRuns[1].result.methodLabel}</span></div>
                  <div><span>1 kg당 변화{comparison?.comparable ? "" : " (조건 검토 필요)"}</span><strong>{comparison?.comparable && compareRuns[0].result.perKgH2 !== null && compareRuns[1].result.perKgH2 !== null ? `${deltaText(compareRuns[1].result.perKgH2 - compareRuns[0].result.perKgH2)} kgCO2eq/kgH2` : "직접 비교 불가"}</strong></div>
                  <div><span>총량 변화</span><strong>{deltaText(compareRuns[1].result.totalKgCO2eq - compareRuns[0].result.totalKgCO2eq)} kgCO2eq</strong></div>
                </div>
                <div className="workspace-comparison-section">
                  <div className="workspace-card-head"><div><h3 className="workspace-card-title">단계별 배출량 변화</h3><p className="workspace-card-desc">양수는 비교 기록에서 증가한 값입니다.</p></div></div>
                  <div className="table-scroll"><table className="admin-table admin-table-compact"><thead><tr><th>단계</th><th>기준 기록</th><th>비교 기록</th><th>변화</th></tr></thead><tbody>{compareRuns[0].result.byStage.map((stage) => { const other = compareRuns[1].result.byStage.find((item) => item.stage === stage.stage); const delta = other ? other.kgCO2eq - stage.kgCO2eq : null; return <tr key={stage.stage}><td>{STAGE_LABEL[stage.stage]}</td><td className="num">{numberText(stage.kgCO2eq)} kgCO2eq</td><td className="num">{numberText(other?.kgCO2eq)} kgCO2eq</td><td className={`num ${comparison?.comparable && delta !== null && delta > 0 ? "text-tier-tight" : comparison?.comparable && delta !== null && delta < 0 ? "text-tier-ample" : "text-ink-muted"}`}>{deltaText(delta)} kgCO2eq</td></tr>; })}</tbody></table></div>
                </div>
                <div className="workspace-comparison-section">
                  <div className="workspace-card-head"><div><h3 className="workspace-card-title">활동자료 수치 변화</h3><p className="workspace-card-desc">설비 이름과 활동자료 항목을 기준으로 비교합니다.</p></div></div>
                  {(() => { const rows = changedInputs(compareRuns[0], compareRuns[1]); return rows.length === 0 ? <p className="workspace-empty">활동자료의 수치 변화가 없습니다. 산정조건·경계·배출계수의 차이는 별도 표에서 확인하세요.</p> : <div className="table-scroll"><table className="admin-table admin-table-compact"><thead><tr><th>설비</th><th>항목</th><th>기준 기록</th><th>비교 기록</th><th>변화</th></tr></thead><tbody>{rows.map((row) => <tr key={`${row.equipment}-${row.label}-${row.unit}`}><td>{row.equipment}</td><td>{row.label}</td><td className="num">{amountText(row.left, row.unit)}</td><td className="num">{amountText(row.right, row.unit)}</td><td className="num text-ink-muted">{deltaText(row.delta)} {row.unit}</td></tr>)}</tbody></table></div>; })()}
                </div>
                <div className="workspace-comparison-section">
                  <MasterDataComparison left={compareRuns[0].masterSnapshot} right={compareRuns[1].masterSnapshot} />
                </div>
              </section>
            )}

            {selectedRun && (
              <section className="workspace-card workspace-run-detail" aria-labelledby="run-detail-title">
                <div className="workspace-card-head">
                  <div>
                    <h2 id="run-detail-title" className="workspace-card-title">실행 기록 상세</h2>
                    <p className="workspace-card-desc">{dateText(selectedRun.createdAt)}에 저장된 입력과 결과입니다.</p>
                  </div>
                  <div className="workspace-actions">
                    <ButtonLink to={`/workspace/projects/${projectId}/runs/${selectedRun.id}/report`}>실행 기록 보고서</ButtonLink>
                    <Button variant="secondary" onClick={loadRunScenario}>이 실행으로 산정 화면 열기</Button>
                    <Button variant="soft" onClick={() => void rerunSelected()}>현재 기준으로 다시 실행</Button>
                    <Button onClick={() => void updateRun({ isBaseline: true })}>기준 실행으로 지정</Button>
                    <Button onClick={() => setSelectedRun(null)}>닫기</Button>
                  </div>
                </div>
                {selectedRun.engineVersion !== ENGINE_VERSION && <p role="status" className="note note-warn text-footnote">이전 엔진에서 저장한 결과입니다. 기능단위 계산과 검증 규칙이 변경되었으므로 현재 기준으로 다시 실행한 기록으로 판단하세요. 아래 값은 저장 당시 값입니다.</p>}
                <div className="workspace-run-detail-grid">
                  <dl className="workspace-run-detail-list">
                    <div><dt>실행 ID</dt><dd>{selectedRun.id}</dd></div>
                    <div><dt>시나리오</dt><dd>{selectedRun.inputSnapshot.name}</dd></div>
                    <div><dt>기준연도</dt><dd>{selectedRun.inputSnapshot.meta.baseYear}</dd></div>
                    <div><dt>산정방식</dt><dd>{selectedRun.result.methodLabel}</dd></div>
                    <div><dt>설비 수</dt><dd>{selectedRun.inputSnapshot.equipment.length}개</dd></div>
                  </dl>
                  <dl className="workspace-run-detail-list">
                    <div><dt>총 온실가스 배출량</dt><dd>{numberText(selectedRun.result.totalKgCO2eq)} kgCO2eq</dd></div>
                    <div><dt>1 kg당 배출량</dt><dd>{numberText(selectedRun.result.perKgH2)} kgCO2eq/kgH2</dd></div>
                    <div><dt>기능단위 수소 {selectedRun.result.functionalUnitKg} kg의 배출량</dt><dd>{numberText(selectedRun.result.perFunctionalUnitKgCO2eq)} kgCO2eq/FU</dd></div>
                    <div><dt>검증 이슈</dt><dd>{selectedRun.result.issues.length}건</dd></div>
                    <div><dt>상태</dt><dd>{selectedRun.status === "ok" ? "계산 가능" : "확인 필요"}</dd></div>
                  </dl>
                </div>
                <div className="workspace-run-detail-meta">
                  <span>엔진 {selectedRun.engineVersion}</span>
                  <span>마스터 데이터 지문 {selectedRun.masterFingerprint}</span>
                  {selectedRun.isBaseline && <span className="badge-accent">기준 실행</span>}
                </div>
                <div className="workspace-run-review">
                  <SelectField label="검토 상태" value={selectedRun.reviewStatus} onChange={(event) => void updateRun({ reviewStatus: event.target.value as "draft" | "reviewing" | "approved" })}>
                    <option value="draft">초안</option><option value="reviewing">검토 중</option><option value="approved">검토 완료</option>
                  </SelectField>
                  <TextArea label="검토 메모" value={selectedRun.note ?? ""} onChange={(event) => setSelectedRun((current) => current ? { ...current, note: event.target.value } : current)} />
                  <Button variant="secondary" onClick={() => void updateRun({ note: selectedRun.note ?? null })}>메모 저장</Button>
                </div>
                <div className="workspace-run-detail-results">
                  <div className="workspace-card-head"><div><h3 className="workspace-card-title">단계별 결과</h3><p className="workspace-card-desc">저장 당시 계산 결과를 다시 확인합니다.</p></div></div>
                  <div className="table-scroll"><table className="admin-table admin-table-compact"><thead><tr><th>단계</th><th>배출량</th><th>비율</th></tr></thead><tbody>{selectedRun.result.byStage.map((stage) => <tr key={stage.stage}><td>{STAGE_LABEL[stage.stage]}</td><td className="num">{numberText(stage.kgCO2eq)} kgCO2eq</td><td className="num">{Math.round(stage.share * 1000) / 10}%</td></tr>)}</tbody></table></div>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
